import { useMemo, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { TNavigationStack } from 'app/types';
import { ActivityName } from 'app/_modules/core/types';
import { StepType, ICustomButton, IWebSocketMeasurementConfig } from '@shared/components/form/types';
import {
  TutorialStep,
  BeforeMeasurementStep,
  MeasurementResultStep,
  IMeasurementResultStepContext,
  IBeforeMeasurementStepContext,
} from '../components/activity/steps';
import { getBeforeMeasurementStepTranslations, getTutorialStepTranslations } from 'app/_modules/core/utils';
import { TMeasurementResponse } from 'app/_modules/pathways/entities';
import { IProcessedMeasurement } from 'app/types/measurement';
import { toMeasurementResponse } from '@shared/utils/webSocketUtils';
import { useActivitySequence } from '@shared/hooks';

export enum EActivitySubmitSteps {
  TUTORIAL_STEP = 'tutorialStep',
  BEFORE_MEASUREMENT_STEP = 'beforeMeasurementStep',
  MEASUREMENT_RESULT_STEP = 'measurementResultStep',
}

interface IUseMeasurementFlowParams {
  activityName: ActivityName;
  activityId: string;
  initialStep?: number;
  forceReset?: boolean;
  isRemeasurement?: boolean;
  autoStartWebSocket?: boolean;
}

export const useMeasurementFlow = ({
  activityName,
  activityId,
  initialStep = 0,
  forceReset = false,
  isRemeasurement = false,
  autoStartWebSocket = false,
}: IUseMeasurementFlowParams) => {
  const navigation = useNavigation<NativeStackNavigationProp<TNavigationStack>>();
  const [measurementResult, setMeasurementResult] = useState<TMeasurementResponse | undefined>();
  const { hasRemainingActivitiesInCurrentPeriod, navigateToNextOrHome } = useActivitySequence(activityId);

  const backButton: ICustomButton = {
    text: isRemeasurement ? 'screens.remeasurement.back-to-start' : 'measurement.goHome',
    onPress: () => (isRemeasurement ? navigation.goBack() : navigation.navigate('Homepage')),
    variant: 'link',
  };

  const hasRemaining = hasRemainingActivitiesInCurrentPeriod();

  const finishButton: ICustomButton | undefined = hasRemaining
    ? { text: 'measurement.nextActivity', onPress: navigateToNextOrHome, variant: 'primary' }
    : undefined;

  const tutorialStep: StepType<EActivitySubmitSteps.TUTORIAL_STEP, undefined, { activityName: ActivityName }> = {
    component: TutorialStep,
    key: EActivitySubmitSteps.TUTORIAL_STEP,
    translations: getTutorialStepTranslations(activityName),
    showBackButton: false,
    showNextButton: activityName !== ActivityName.STEP,
    context: { activityName },
  };

  const beforeMeasurementStep: StepType<EActivitySubmitSteps.BEFORE_MEASUREMENT_STEP, undefined, IBeforeMeasurementStepContext> = {
    component: BeforeMeasurementStep,
    key: EActivitySubmitSteps.BEFORE_MEASUREMENT_STEP,
    translations: getBeforeMeasurementStepTranslations(activityName),
    context: { activityName, activityId },
    showBackButton: false,
  };

  const measurementResultStep: StepType<EActivitySubmitSteps.MEASUREMENT_RESULT_STEP, undefined, IMeasurementResultStepContext> = {
    component: MeasurementResultStep,
    key: EActivitySubmitSteps.MEASUREMENT_RESULT_STEP,
    context: { activityName, alertType: undefined, result: measurementResult, isRemeasurement },
  };

  const steps = [tutorialStep, beforeMeasurementStep, measurementResultStep];

  const webSocketConfig: IWebSocketMeasurementConfig = useMemo(() => ({
    activityName,
    activityId,
    resultStepKey: EActivitySubmitSteps.MEASUREMENT_RESULT_STEP,
    beforeMeasurementStepKey: EActivitySubmitSteps.BEFORE_MEASUREMENT_STEP,
    onMeasurementReceived: (measurement: IProcessedMeasurement) => {
      setMeasurementResult(toMeasurementResponse(measurement));
    },
    autoStart: autoStartWebSocket,
  }), [activityName, activityId, autoStartWebSocket]);

  return {
    steps,
    webSocketConfig,
    backButton,
    finishButton,
    resolvedInitialStep: forceReset ? 0 : initialStep,
    handleFinish: navigateToNextOrHome,
  };
};
